import {Component, forwardRef, Input, OnInit} from '@angular/core';
import { NG_VALUE_ACCESSOR, Validators, FormControl, FormGroup, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Subscription} from 'rxjs';
import {
  createNoDecimalInputMask,
  createTwoDecimalInputMask,
  formControlErrorKeys,
  formControlErrorMessage,
  fromRateCardCell,
  rateOrRelativeRateValidator,
  toRateCardCell,
  Mark
} from '@portal-workspace/grow-ui-library';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {delay, distinctUntilChanged, tap} from 'rxjs/operators';
import numeral from 'numeral';
import {AssetTier2Value, compareMatch} from '@portal-workspace/grow-shared-library';
import {MARK} from '@portal-workspace/grow-ui-library/mark';
import { MatDividerModule } from '@angular/material/divider';
import { FlexModule } from '@angular/flex-layout/flex';
import { InputMaskModule } from '@ngneat/input-mask';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';


// ZEN-XXX: 4-bucket AF Tier 2 rate card (structurally identical to Tier 1):
// a single `New` rate plus three editable age-based `Used` buckets.
@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    selector: 'asset-tier2',
    templateUrl: './asset-tier2.component.html',
    styleUrls: ['./asset-tier2.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AssetTier2Component), multi: true },
        { provide: MARK, useExisting: forwardRef(() => AssetTier2Component), multi: false },
    ],
    standalone: true,
  imports: [MatFormFieldModule, MatInputModule, FormsModule, InputMaskModule, ReactiveFormsModule, FlexModule, MatDividerModule]
})
export class AssetTier2Component extends AbstractControlValueAccessor<AssetTier2Value> implements OnInit, Mark {

  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;

  @Input({required: false}) title: string = '';
  @Input({required: false}) maxYear: number = 20;

  subscriptions: Subscription[] = [];

  createNoDecimalInputMask = createNoDecimalInputMask();
  createTwoDecimalInputMask = createTwoDecimalInputMask();

  formGroup: FormGroup<{
    newRate: FormControl<number | null>,
    year1: FormControl<number | null>,
    year2: FormControl<number | null>,
    assetBackedLowYear: FormControl<string | null>,
    assetBackedMediumYear: FormControl<string | null>,
    assetBackedHighYear: FormControl<string | null>,
  }>;
  formControlNewRate: FormControl<number | null>;
  formControlYear1: FormControl<number | null>;
  formControlYear2: FormControl<number | null>;
  formControlAssetBackedLowYear: FormControl<string | null>;
  formControlAssetBackedMediumYear: FormControl<string | null>;
  formControlAssetBackedHighYear: FormControl<string | null>;

  constructor(private formBuilder: FormBuilder) {
    super();
    this.formControlNewRate = formBuilder.control(0, [Validators.required]);
    this.formControlYear1 = formBuilder.control(4, [Validators.required]);
    this.formControlYear2 = formBuilder.control(10, [Validators.required]);
    this.formControlAssetBackedLowYear = formBuilder.control<string | null>('0', [Validators.required, rateOrRelativeRateValidator]);
    this.formControlAssetBackedMediumYear = formBuilder.control<string | null>('0', [Validators.required, rateOrRelativeRateValidator]);
    this.formControlAssetBackedHighYear = formBuilder.control<string | null>('0', [Validators.required, rateOrRelativeRateValidator]);

    this.formGroup = formBuilder.group({
      newRate: this.formControlNewRate,
      year1: this.formControlYear1,
      year2: this.formControlYear2,
      assetBackedLowYear: this.formControlAssetBackedLowYear,
      assetBackedMediumYear: this.formControlAssetBackedMediumYear,
      assetBackedHighYear: this.formControlAssetBackedHighYear,
    });
  }

  ngOnInit() {
   setupUntilDestroy(this);
    const sub = this.formGroup.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(r  => {
        if (this.formGroup.valid) {
          const v: Exclude<AssetTier2Value, null> = {
            YearRangeThresholds: [String(r.year1), String(r.year2)],
            AssetBacked: {
              New: { Rate: (this.formControlNewRate.value ?? 0).toString() },
              YearRanges: [
                {
                  YearRange: `Used to ${r.year1} years`,
                  YearRangeType: 'Low',
                  Operator: '<=',
                  Threshold: `${r.year1}`,
                  Rate: toRateCardCell(r.assetBackedLowYear),
                },
                {
                  YearRange: `${this.parseToInt(r.year1) + 1} to ${r.year2} years`,
                  YearRangeType: 'Medium',
                  Operator: '-',
                  Threshold: `${this.parseToInt(r.year1) + 1}-${r.year2}`,
                  Rate: toRateCardCell(r.assetBackedMediumYear),
                },
                {
                  YearRange: `${this.parseToInt(r.year2) + 1} to ${this.maxYear} years`,
                  YearRangeType: 'High',
                  Operator: '>',
                  Threshold: `${r.year2}`,
                  Rate: toRateCardCell(r.assetBackedHighYear),
                },
              ]
            },
          };
          this.propagateChange(v);
        } else {
          this.propagateChange(null);
        }
      })).subscribe();
  }

  doWriteValue(v: AssetTier2Value | null | undefined): void | AssetTier2Value {
    if (v) {
      const newRate = v.AssetBacked?.New?.Rate;
      if (newRate != null) {
        this.formControlNewRate.setValue(numeral(newRate).value());
      }
      if (v.YearRangeThresholds && v.YearRangeThresholds.length) {
        if (v.YearRangeThresholds.length >= 1 && numeral(v.YearRangeThresholds[0]).value()) {
          this.formControlYear1.setValue(numeral(v.YearRangeThresholds[0]).value());
        }
        if (v.YearRangeThresholds.length >= 2 && numeral(v.YearRangeThresholds[1]).value()) {
          this.formControlYear2.setValue(numeral(v.YearRangeThresholds[1]).value());
        }
      }
      if (v.AssetBacked) {
        const lowYear = (v.AssetBacked.YearRanges ?? []).find(opt => opt.YearRangeType === 'Low');
        const lowYearRate = lowYear ? fromRateCardCell(lowYear.Rate) : null;
        if (lowYearRate != null) {
          this.formControlAssetBackedLowYear.setValue(lowYearRate);
        }
        const mediumYear = (v.AssetBacked.YearRanges ?? []).find(opt => opt.YearRangeType === 'Medium');
        const mediumYearRate = mediumYear ? fromRateCardCell(mediumYear.Rate) : null;
        if (mediumYearRate != null) {
          this.formControlAssetBackedMediumYear.setValue(mediumYearRate);
        }
        const highYear = (v.AssetBacked.YearRanges ?? []).find(opt => opt.YearRangeType === 'High');
        const highYearRate = highYear ? fromRateCardCell(highYear.Rate) : null;
        if (highYearRate != null) {
          this.formControlAssetBackedHighYear.setValue(highYearRate);
        }
      }
    }
    return undefined;
  }

  parseToInt(i: any): number {
    return parseInt(i, 10);
  }

  mark(): void {
    this.formGroup.markAllAsTouched();
  }
}
